import React, { useState } from 'react';
import { Phone, MessageCircle } from 'lucide-react';
import { STORES, WHATSAPP_NUMBER, buildAppointmentUrl } from '../mock';
import { toast } from 'sonner';

const TABS = ['Appointment', 'Contact'];

const AppointmentTab = () => {
  const [form, setForm] = useState({ name: '', phone: '', store: STORES[0].city, date: '', notes: '' });
  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.date.trim()) {
      toast.error('Please fill in your name, phone, and preferred date.');
      return;
    }
    const url = buildAppointmentUrl(form);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="max-w-[600px] mx-auto">
      <p className="text-sm text-[#7a6a54] text-center mb-10">
        Book a 30-minute in-store or virtual consultation with one of our jewellery consultants.
        We'll confirm your appointment over WhatsApp.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div>
          <label className="text-[10px] tracking-widest-2 uppercase text-[#7a6a54]">Name</label>
          <input
            type="text"
            required
            value={form.name}
            onChange={update('name')}
            placeholder="Your name"
            className="w-full bg-transparent border-b border-[#d8ceb9] focus:border-[#1a1a1a] outline-none py-2 text-[#1a1a1a] placeholder:text-[#a99f8c] transition-colors"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-widest-2 uppercase text-[#7a6a54]">Phone</label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={update('phone')}
            placeholder="+91 98765 43210"
            className="w-full bg-transparent border-b border-[#d8ceb9] focus:border-[#1a1a1a] outline-none py-2 text-[#1a1a1a] placeholder:text-[#a99f8c] transition-colors"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-widest-2 uppercase text-[#7a6a54]">Preferred Store</label>
          <select
            value={form.store}
            onChange={update('store')}
            className="w-full bg-transparent border-b border-[#d8ceb9] focus:border-[#1a1a1a] outline-none py-2 text-[#1a1a1a] transition-colors"
          >
            {STORES.map((s) => (
              <option key={s.city} value={s.city}>{s.city}</option>
            ))}
            <option value="Virtual">Virtual Consultation</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] tracking-widest-2 uppercase text-[#7a6a54]">Preferred Date</label>
          <input
            type="date"
            required
            value={form.date}
            onChange={update('date')}
            className="w-full bg-transparent border-b border-[#d8ceb9] focus:border-[#1a1a1a] outline-none py-2 text-[#1a1a1a] transition-colors"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-widest-2 uppercase text-[#7a6a54]">Notes (optional)</label>
          <textarea
            rows={3}
            value={form.notes}
            onChange={update('notes')}
            placeholder="Anything you'd like us to know"
            className="w-full bg-transparent border-b border-[#d8ceb9] focus:border-[#1a1a1a] outline-none py-2 text-[#1a1a1a] placeholder:text-[#a99f8c] transition-colors resize-none"
          />
        </div>
        <button
          type="submit"
          className="mt-4 w-full bg-[#1a1a1a] text-[#FFFFFF] py-4 text-[11px] tracking-widest-2 uppercase hover:bg-[#3a342b] transition-colors"
        >
          Request Appointment
        </button>
      </form>
    </div>
  );
};

const ContactTab = () => (
  <div className="max-w-[500px] mx-auto text-center flex flex-col gap-6">
    <p className="text-sm text-[#7a6a54]">We'd love to hear from you. Reach us through any of the channels below.</p>
    <a href="tel:+918722447768" className="flex items-center justify-center gap-2 font-serif text-xl text-[#1a1a1a] hover:text-[#999959]">
      <Phone size={18} strokeWidth={1.4} /> +91 8722447768
    </a>
    <a href="mailto:hello@houseofseya.com" className="font-serif text-xl text-[#1a1a1a] hover:text-[#999959]">
      hello@houseofseya.com
    </a>
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center gap-2 text-[11px] tracking-widest-2 uppercase text-[#1a1a1a] border-b border-[#1a1a1a] pb-1 hover:text-[#999959] hover:border-[#999959] mx-auto"
    >
      <MessageCircle size={14} strokeWidth={1.4} /> Message us on WhatsApp
    </a>
  </div>
);

const Appointments = () => {
  const [tab, setTab] = useState('Appointment');

  return (
    <main className="bg-[#FFFFFF] min-h-screen">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-16 pb-10 text-center">
        <h1 className="font-serif text-3xl lg:text-4xl text-[#1a1a1a]">Appointments</h1>

        <div className="flex justify-center gap-4 sm:gap-8 mt-10 border-b border-[#e5ddce] overflow-x-auto no-scrollbar px-4">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`whitespace-nowrap text-[11px] sm:text-[12px] tracking-widest uppercase pb-3 transition-colors ${tab === t ? 'text-[#1a1a1a] border-b-2 border-[#1a1a1a]' : 'text-[#7a6a54] hover:text-[#1a1a1a]'}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-24">
        {tab === 'Appointment' && <AppointmentTab />}
        {tab === 'Contact' && <ContactTab />}
      </div>
    </main>
  );
};

export default Appointments;
